/**
 * Речення з назвою сутності з метаданих (UX-42; metadata-ui.md, розд. 2): «Створити заявку», «Нова
 * заявка», «37 заявок», «Видалити 21 заявку», «Немає заявок». Платформа не знає слова «заявка»: вона бере
 * форми, які дає метадані сутності (`NounUk`: називний, родовий, знахідний, однина й множина, рід), і
 * підставляє їх у шаблони каталогу.
 *
 * - **Запасний шаблон.** Сутність адміністратора може мати лише називний (ADM-106): тоді речення
 *   будується з двокрапкою («Створити: журнал»), а не з неправильним відмінком. Те саме, якщо форм
 *   немає зовсім: нейтральне «записи».
 * - **Імена людей не відмінюються:** це правило тих, хто будує речення з іменем («Подзвонити:
 *   Мар'яна Гнатюк»), тут його немає.
 * - **Число.** Слово після числа вибирає `noun(label, { count })` за категорією CLDR (one · few ·
 *   many · other); 1,5 — родовий однини.
 */
import { capitalize, type NounCase, type NounUk, noun } from '@clientline/contracts/i18n';
import { formatNumber } from './format.ts';
import { words as t } from './uk/words.ts';

export type Label = NounUk | undefined | null;

const FORMS = ['nom', 'gen', 'acc'] as const;

/** Чи є всі шість форм. Порожній рядок у формі — «немає» (адміністратор заповнив лише називний). */
export function hasForms(label: Label): label is NounUk {
  return !!label && FORMS.every((c) => label[c].every((form) => form.trim() !== ''));
}

/** Назва в називному однини для запасного шаблону; без неї — нейтральне слово. */
export const base = (label: Label): string => {
  const nom = label?.nom[0]?.trim();
  return nom ? nom : t.records;
};

/** «Створити демо-задачу»; запасний — «Створити: демо-задача». */
export function createLabel(label: Label): string {
  return hasForms(label) ? t.create(noun(label, { case: 'acc' })) : t.createFallback(base(label));
}

/** Слово посеред фрази: підписи сутностей у метаданих бувають з великої (#1228, К6). */
export const lowerFirst = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);

/** Слово після числа (`37 заявок`, `1 заявка`) або запасне; саме число — у виклику. */
export function countedNoun(
  label: Label,
  count: number,
  grammaticalCase: NounCase = 'nom',
): string {
  return hasForms(label) ? noun(label, { case: grammaticalCase, count }) : base(label);
}

/** «37 заявок», «21 заявка», «1,5 заявки»; запасний — «37: заявка». */
export function countLabel(label: Label, count: number): string {
  return hasForms(label)
    ? `${formatNumber(count)} ${noun(label, { case: 'nom', count })}`
    : t.countFallback(formatNumber(count), base(label));
}

/** «Видалити 21 заявку», «Видалити 37 заявок»; запасний — «Видалити: заявка (37)». */
export function deleteLabel(label: Label, count: number): string {
  return hasForms(label)
    ? t.delete(`${formatNumber(count)} ${noun(label, { case: 'acc', count })}`)
    : t.deleteFallback(base(label), formatNumber(count));
}

/** «Немає заявок» (родовий множини); запасний — «Немає записів». */
export function noneLabel(label: Label): string {
  return t.none(hasForms(label) ? noun(label, { case: 'gen', plural: true }) : t.recordsGen);
}

/** «Заявки» — множина з великої: заголовок списку. */
export function titleLabel(label: Label): string {
  return capitalize(hasForms(label) ? noun(label, { plural: true }) : t.records);
}
